// utils/analyzeLabResult.js

function formatNumberLikeInput(n) {
  return Number.isInteger(n) ? String(n) : String(n);
}

function parseInputToNumber(input) {
  if (typeof input === 'number') return Number.isFinite(input) ? input : null;
  if (input === null || input === undefined) return null;
  const normalized = String(input).replace(',', '.').trim();
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

function inRange(value, min, max) {
  const geMin = min === null || min === undefined || value >= min;
  const leMax = max === null || max === undefined || value <= max;
  return geMin && leMax;
}

function isInSpec(spec, value) {
  const min = spec?.specMin ?? null;
  const max = spec?.specMax ?? null;
  if (min === null && max === null) return false;
  return inRange(value, min, max);
}

function shortZoneTypeFromLabel(label) {
  const l = String(label || '').toLowerCase();
  if (l.includes('b')) return 'B';
  if (l.includes('x')) return 'X';
  return 'OUT';
}

function findMatchingZone(spec, value) {
  const zones = Array.isArray(spec?.labZones) ? [...spec.labZones] : [];
  zones.sort((a, b) => (a?.min ?? -Infinity) - (b?.min ?? -Infinity));
  return (
    zones.find((z) => inRange(value, z?.min ?? null, z?.max ?? null)) || null
  );
}

function makeZoneDisplay(zoneType) {
  if (zoneType === 'B') return 'Strefa B';
  if (zoneType === 'X') return 'Strefa X';
  return 'Poza zakresem';
}

/**
 * @param {object} spec - obiekt LabSpecification (jak w Twoim przykładzie)
 * @param {string|number} resultInput - wynik analizy (np. 1.2)
 * @param {string|number} [bbtInput] - opcjonalny wynik BBT
 */
export function analyzeLabResult(spec, resultInput, bbtInput) {
  const unit = spec?.parameter?.unit || '';
  const paramName = spec?.parameter?.name || '';
  const decisionMaker = spec?.parameter?.decisionMaker || '';

  const result = parseInputToNumber(resultInput);
  const hasBbt = bbtInput !== undefined;
  const bbt = hasBbt ? parseInputToNumber(bbtInput) : null;

  // defaulty N/D dla BBT jeśli nie podano
  let bbtResults = 'N/D';
  let bbtRelease = 'N/D';

  // jeśli wynik nie jest liczbą
  if (result === null) {
    const base = `${String(resultInput)} ${unit}`.trim();
    return {
      analyzeResults: `${base} - brak klasyfikacji`,
      releaseRange: 'N/D',
      releaseZone: 'N/D',
      bbtRelease,
      bbtResults,
      zoneType: 'OUT',
    };
  }

  // --- 1) Jeśli wynik analizy jest w SPEC: specjalne zachowanie ---
  if (isInSpec(spec, result)) {
    const analyzeResults = `${formatNumberLikeInput(result)} ${unit}`.trim();

    // Dla wyniku w SPEC: releaseRange i releaseZone = N/D
    // Ale BBT liczymy normalnie (jeśli podane)
    if (hasBbt && bbt !== null) {
      if (isInSpec(spec, bbt)) {
        bbtResults = `${formatNumberLikeInput(bbt)} ${unit}`.trim();
        bbtRelease = 'N/D';
      } else {
        const bbtZone = findMatchingZone(spec, bbt);
        const bbtType = bbtZone ? shortZoneTypeFromLabel(bbtZone.label) : 'OUT';
        const bbtDisplay = makeZoneDisplay(bbtType);

        bbtResults = `${formatNumberLikeInput(
          bbt,
        )} ${unit} - ${bbtDisplay}`.trim();
        bbtRelease =
          bbtZone && (bbtType === 'B' || bbtType === 'X')
            ? `${bbtZone.rangeLabel} ${unit}`.trim()
            : 'N/D';
      }
    } else {
      bbtResults = 'N/D';
      bbtRelease = 'N/D';
    }

    return {
      analyzeResults,
      releaseRange: 'N/D',
      releaseZone: 'N/D',
      bbtRelease,
      bbtResults,
      zoneType: 'SPEC',
    };
  }

  // --- 2) Wynik nie jest w SPEC -> klasyfikujemy po strefach ---
  let zoneType = 'OUT';
  let matchedZone = findMatchingZone(spec, result);

  if (matchedZone) zoneType = shortZoneTypeFromLabel(matchedZone.label);

  const analyzeResults = `${formatNumberLikeInput(
    result,
  )} ${unit} - ${makeZoneDisplay(zoneType)}`.trim();

  // releaseRange i releaseZone mają sens tylko dla B/X
  const releaseRange =
    matchedZone && (zoneType === 'B' || zoneType === 'X')
      ? `${matchedZone.rangeLabel} ${unit}`.trim()
      : 'N/D';

  const releaseZone =
    zoneType === 'B' || zoneType === 'X'
      ? `${paramName} - ${zoneType} - ${decisionMaker}`.trim()
      : 'N/D';

  // --- 3) BBT: jeśli nie podano -> N/D już ustawione ---
  if (hasBbt && bbt !== null) {
    // klasyfikujemy bbt analogicznie jak wynik
    if (isInSpec(spec, bbt)) {
      // jeśli BBT w spec: bbtResults wg Twoich reguł? (nie podałeś wprost)
      // Przyjmuję: skoro w spec, to bez strefy, a release (zakres) N/D
      bbtResults = `${formatNumberLikeInput(bbt)} ${unit}`.trim();
      bbtRelease = 'N/D';
    } else {
      const bbtZone = findMatchingZone(spec, bbt);
      const bbtType = bbtZone ? shortZoneTypeFromLabel(bbtZone.label) : 'OUT';

      bbtResults = `${formatNumberLikeInput(bbt)} ${unit} - ${makeZoneDisplay(
        bbtType,
      )}`.trim();
      bbtRelease =
        bbtZone && (bbtType === 'B' || bbtType === 'X')
          ? `${bbtZone.rangeLabel} ${unit}`.trim()
          : 'N/D';
    }
  }

  return {
    analyzeResults,
    releaseRange,
    releaseZone,
    bbtRelease,
    bbtResults,
    zoneType,
  };
}
